import type {
  BoardPickerOption,
  BoardPickerParent,
} from "../../../component-library/board-picker/BoardPicker.js";
import {
  boards,
  getBoard,
  getParentBoard,
  parentBoards,
  type Board,
} from "@/entities/board/index.js";
import type { Post } from "@/entities/post/index.js";

export const composeBoardParents: BoardPickerParent[] = parentBoards.map((parent) => ({
  id: parent.id,
  label: parent.name,
}));

export function getComposeBoardOptions({
  query = "",
  selectedBoardIds = [],
}: {
  query?: string;
  selectedBoardIds?: string[];
} = {}): BoardPickerOption[] {
  const selected = new Set(normalizeComposeBoardIds(selectedBoardIds));
  const q = query.trim().toLowerCase();

  return boards
    .filter((board) => !q || boardSearchText(board).includes(q))
    .map((board) => {
      const parent = getParentBoard(board.parentId);
      return {
        id: board.id,
        title: board.name,
        parentId: board.parentId,
        parentLabel: parent?.name,
        boardIds: [board.id],
        recommended: selected.has(board.id) || isRecommendedBoard(board.id),
      };
    });
}

export function normalizeComposeBoardIds(boardIds: readonly string[]): string[] {
  const ids: string[] = [];
  boardIds.forEach((boardId) => {
    if (getBoard(boardId) && !ids.includes(boardId)) ids.push(boardId);
  });
  return ids;
}

export function parentIdForBoardId(boardId: string): string {
  return getBoard(boardId)?.parentId ?? parentBoards[0]?.id ?? "daily";
}

export function targetSummaryForBoardIds(
  boardIds: readonly string[],
  primaryBoardId = boardIds[0] ?? "",
): string {
  const ids = normalizeComposeBoardIds(boardIds);
  const primary = getBoard(primaryBoardId) ?? getBoard(ids[0] ?? "") ?? boards[0];
  if (!primary) return "选择板块";

  const extraCount = ids.filter((boardId) => boardId !== primary.id).length;
  return extraCount > 0 ? `${primary.name} +${extraCount}` : primary.name;
}

export function identityChoicesForBoardIds(boardIds: readonly string[]) {
  if (requiresAnonymousIdentity(boardIds)) {
    return [{ id: "anonymous", label: "匿名" }];
  }
  return [
    { id: "anonymous", label: "匿名" },
    { id: "public", label: "公开" },
  ];
}

export function identityModeForBoardIds(
  boardIds: readonly string[],
  currentIdentity: Post["identityMode"],
): Post["identityMode"] {
  if (requiresAnonymousIdentity(boardIds)) return "anonymous";
  return currentIdentity === "public" ? "public" : "anonymous";
}

export function isIdentityModeAllowedForBoardIds(
  boardIds: readonly string[],
  identityMode: Post["identityMode"],
): boolean {
  return !requiresAnonymousIdentity(boardIds) || identityMode === "anonymous";
}

export function identityHintForBoardIds(boardIds: readonly string[]): string {
  const ids = normalizeComposeBoardIds(boardIds);
  if (ids.length === 0) return "至少选择一个板块。";

  if (ids.length === 1) {
    const board = getBoard(ids[0] ?? "");
    if (board?.identityPolicy === "force_anonymous") return "这个板块要求匿名发布。";
    return "这个板块默认公开，也可以改为匿名。";
  }

  const names = ids
    .map((boardId) => getBoard(boardId)?.name)
    .filter(Boolean)
    .join("、");
  if (requiresAnonymousIdentity(ids)) return `${names} 的规则已合并，本次只能匿名发布。`;
  return `${names} 的规则兼容，可选择匿名或公开。`;
}

export function composeBoardSelectionMessage(boardIds: readonly string[]): string {
  const ids = normalizeComposeBoardIds(boardIds);
  const names = ids
    .slice(1)
    .map((boardId) => getBoard(boardId)?.name)
    .filter(Boolean);
  if (!names.length) return "默认只贴到当前发布目标，同发在角落展开。";
  return `同发：${names.join("、")}。其他板块自动位置，首页不重复。`;
}

function requiresAnonymousIdentity(boardIds: readonly string[]): boolean {
  return normalizeComposeBoardIds(boardIds).some(
    (boardId) => getBoard(boardId)?.identityPolicy === "force_anonymous",
  );
}

function boardSearchText(board: Board): string {
  const parent = getParentBoard(board.parentId);
  return [board.name, board.desc, parent?.name, parent?.desc]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isRecommendedBoard(boardId: string): boolean {
  return ["life", "emotion", "study", "help"].includes(boardId);
}
